import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { trackAddToCart, trackAddToWishlist } from "../lib/analytics";
import api from "../lib/api";

// Fire-and-forget backend engagement log (powers admin Engagement + Recovery emails)
const logEvent = (name, props) => {
  api.post("/events", { name, props }).catch(() => {});
};

const ShopCtx = createContext(null);

const load = (k, def) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : def; } catch { return def; }
};

export function ShopProvider({ children }) {
  const [cart, setCart] = useState(() => load("goshi_cart", []));
  const [wishlist, setWishlist] = useState(() => load("goshi_wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [coupon, setCoupon] = useState(() => load("goshi_coupon", null));
  // Tracks the most recently auto-removed coupon so the cart drawer can render a
  // "keep shopping to unlock" nudge. Session-only — cleared once the shopper
  // qualifies again (auto re-apply) OR they dismiss it OR clear the cart.
  const [pendingCoupon, setPendingCoupon] = useState(null);

  useEffect(() => { localStorage.setItem("goshi_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("goshi_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => {
    if (coupon) localStorage.setItem("goshi_coupon", JSON.stringify(coupon));
    else localStorage.removeItem("goshi_coupon");
    // Clear the nudge pill once ANY coupon is successfully applied
    if (coupon) setPendingCoupon(null);
  }, [coupon]);

  const addToCart = useCallback((product, size, qty = 1) => {
    trackAddToCart(product, qty, size);
    logEvent("add_to_cart", { product_id: product.product_id, name: product.name, price: product.price, size, qty });
    setCart(prev => {
      const idx = prev.findIndex(i => i.product_id === product.product_id && i.size === size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...prev, {
        product_id: product.product_id,
        name: product.name,
        price: product.price,
        image: product.images?.[0],
        size,
        quantity: qty,
      }];
    });
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((product_id, size, qty) => {
    setCart(prev => prev.map(i =>
      i.product_id === product_id && i.size === size ? { ...i, quantity: Math.max(1, qty) } : i
    ));
  }, []);

  const removeFromCart = useCallback((product_id, size) => {
    setCart(prev => prev.filter(i => !(i.product_id === product_id && i.size === size)));
  }, []);

  const clearCart = useCallback(() => { setCart([]); setCoupon(null); setPendingCoupon(null); }, []);

  const toggleWishlist = useCallback((product) => {
    setWishlist(prev => {
      const exists = prev.some(p => p.product_id === product.product_id);
      if (exists) return prev.filter(p => p.product_id !== product.product_id);
      trackAddToWishlist(product);
      logEvent("wishlist_add", { product_id: product.product_id, name: product.name, price: product.price });
      return [...prev, {
        product_id: product.product_id,
        name: product.name,
        price: product.price,
        image: product.images?.[0],
      }];
    });
  }, []);

  const inWishlist = useCallback((pid) => wishlist.some(p => p.product_id === pid), [wishlist]);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  // Auto-revalidate the applied coupon whenever the cart changes.
  // Handles two cases the user asked for:
  //   1) Discount updates (esp. BXGY, whose discount_amount is cart-shape dependent)
  //   2) Coupon auto-removes if the new subtotal falls below the coupon's min_order,
  //      or the cart no longer qualifies (e.g. removed the required BXGY item).
  // Debounced 250ms so a rapid qty +/- doesn't hammer the server; guarded against
  // stale responses via a monotonically-increasing request id.
  const revalRef = useRef({ id: 0, code: null });
  useEffect(() => {
    if (!coupon?.code) return;
    if (cart.length === 0) { setCoupon(null); return; }
    const rid = ++revalRef.current.id;
    revalRef.current.code = coupon.code;
    const t = setTimeout(async () => {
      try {
        const items = cart.map(i => ({ product_id: i.product_id, price: i.price, quantity: i.quantity }));
        const { data } = await api.post("/coupons/validate", { code: coupon.code, subtotal, items });
        if (rid !== revalRef.current.id) return; // stale — a newer change fired
        // Only re-set if something meaningfully changed to avoid a state-write loop
        const changed = (data.discount_amount ?? null) !== (coupon.discount_amount ?? null)
                     || (data.discount_percent ?? null) !== (coupon.discount_percent ?? null);
        if (changed) setCoupon(data);
      } catch (err) {
        if (rid !== revalRef.current.id) return;
        // Coupon no longer valid for the current cart — auto-remove with a soft toast
        const reason = err?.response?.data?.detail || "Cart no longer qualifies";
        const removed = coupon;
        setCoupon(null);
        // Remember what was removed so the cart drawer can render a nudge pill
        setPendingCoupon({ code: removed.code, reason, at: Date.now() });
        toast.message(`Coupon ${removed.code} removed — ${reason}`);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, subtotal, coupon?.code]);

  // If the shopper adds enough to qualify again, clear the pending nudge —
  // the auto-apply banner in the cart drawer will now re-suggest the coupon.
  useEffect(() => {
    if (!pendingCoupon || !pendingCoupon.code) return;
    if (cart.length === 0) { setPendingCoupon(null); return; }
    const items = cart.map(i => ({ product_id: i.product_id, price: i.price, quantity: i.quantity }));
    const t = setTimeout(() => {
      api.post("/coupons/validate", { code: pendingCoupon.code, subtotal, items })
        .then(() => setPendingCoupon(null))
        .catch(() => {}); // still doesn't qualify — leave the pill up
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, subtotal, pendingCoupon?.code]);

  const dismissPendingCoupon = useCallback(() => setPendingCoupon(null), []);

  // Use server-authored discount_amount when present (bxgy / flat_off / percent all supported).
  // Fall back to percent math for legacy coupons that pre-date the discount_amount field.
  const discount = coupon
    ? (typeof coupon.discount_amount === "number"
        ? Math.min(subtotal, coupon.discount_amount)
        : (subtotal * (coupon.discount_percent || 0)) / 100)
    : 0;
  // Shipping: complimentary if the net order (after discount) is ≥ ₹1,000, else ₹100 flat.
  // Keep in sync with backend /payments/razorpay/order — that is the authoritative source.
  const netBeforeShipping = Math.max(0, subtotal - discount);
  const shippingFee = netBeforeShipping > 0 && netBeforeShipping < 1000 ? 100 : 0;
  const total = netBeforeShipping + shippingFee;

  const value = useMemo(() => ({
    cart, cartCount, subtotal, discount, shippingFee, total, coupon, setCoupon,
    cartOpen, setCartOpen,
    pendingCoupon, dismissPendingCoupon,
    addToCart, updateQty, removeFromCart, clearCart,
    wishlist, toggleWishlist, inWishlist,
  }), [cart, cartCount, subtotal, discount, shippingFee, total, coupon, cartOpen,
       pendingCoupon, dismissPendingCoupon,
       addToCart, updateQty, removeFromCart, clearCart,
       wishlist, toggleWishlist, inWishlist]);

  return (
    <ShopCtx.Provider value={value}>
      {children}
    </ShopCtx.Provider>
  );
}

export const useShop = () => useContext(ShopCtx);
